import axios from 'axios';

function unwrap(res) {
  const body = res?.data;
  if (body && typeof body === 'object' && body.success === true) {
    return body.data ?? null;
  }
  return body ?? null;
}

export function formatApiError(err, fallback = 'Request failed') {
  if (err?.response?.status === 401) {
    return 'Session expired. Please sign in again.';
  }
  if (err?.response?.status === 404) {
    return err?.response?.data?.detail || 'Not found. Deploy the latest backend if this is a new feature.';
  }
  if (err?.response?.status === 413) {
    return 'File is too large for upload (max ~4 MB). Compress the image/PDF and try again.';
  }
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || JSON.stringify(d)).join('; ');
  }
  if (detail && typeof detail === 'object') {
    return detail.message || JSON.stringify(detail);
  }
  return err?.message || fallback;
}

/** Vercel serverless request body limit is ~4.5 MB — keep uploads under this. */
export const TEMPLATE_MEDIA_MAX_BYTES = 4 * 1024 * 1024;

export async function fetchCampaignOverview(apiUrl) {
  const res = await axios.get(`${apiUrl}/api/campaigns/overview`);
  return unwrap(res);
}

export async function fetchMonthlyCampaignOverview(apiUrl) {
  const res = await axios.get(`${apiUrl}/api/campaigns/monthly-overview`);
  return unwrap(res);
}

export async function listBulkCampaigns(apiUrl, params = {}) {
  const res = await axios.get(`${apiUrl}/api/campaigns/bulk`, { params });
  return unwrap(res);
}

export async function createCampaign(apiUrl, payload) {
  const res = await axios.post(`${apiUrl}/api/campaigns`, payload);
  return unwrap(res);
}

/** Schedule a draft campaign, or move a scheduled one. schedule = {days_from_now, time: 'HH:MM' IST}. */
export async function scheduleCampaign(apiUrl, campaignId, schedule) {
  const res = await axios.patch(`${apiUrl}/api/campaigns/${campaignId}/schedule`, schedule);
  return unwrap(res);
}

/** Take a scheduled campaign back to draft. Nothing is sent. */
export async function cancelCampaignSchedule(apiUrl, campaignId) {
  const res = await axios.post(`${apiUrl}/api/campaigns/${campaignId}/cancel-schedule`);
  return unwrap(res);
}

const LONG_SEND_TIMEOUT_MS = 310000;

export async function executeCampaign(apiUrl, campaignId) {
  const res = await axios.post(
    `${apiUrl}/api/campaigns/${campaignId}/execute`,
    {},
    { timeout: LONG_SEND_TIMEOUT_MS },
  );
  return unwrap(res);
}

/** Queue this campaign's failed numbers for a same-template retry. */
export async function resendFailedCampaign(apiUrl, campaignId) {
  const res = await axios.post(`${apiUrl}/api/campaigns/${campaignId}/resend-failed`);
  return unwrap(res);
}

/** Sends the next bounded batch of pending recipients. Campaigns send in batches, not
 * one shot, so the caller polls this until the returned `remaining` is 0. */
export async function processCampaignBatch(apiUrl, campaignId) {
  const res = await axios.post(
    `${apiUrl}/api/campaigns/${campaignId}/process-batch`,
    {},
    { timeout: LONG_SEND_TIMEOUT_MS },
  );
  return unwrap(res);
}

export async function getCampaign(apiUrl, campaignId) {
  const res = await axios.get(`${apiUrl}/api/campaigns/${campaignId}`);
  return unwrap(res);
}

export async function getCampaignAnalytics(apiUrl, campaignId) {
  const res = await axios.get(`${apiUrl}/api/campaigns/${campaignId}/analytics`);
  return unwrap(res);
}

export async function getCampaignPreview(apiUrl, campaignId) {
  const res = await axios.get(`${apiUrl}/api/campaigns/${campaignId}/preview`);
  return unwrap(res);
}

export async function listCampaignRecipients(apiUrl, campaignId, params = {}) {
  const res = await axios.get(`${apiUrl}/api/campaigns/${campaignId}/recipients`, { params });
  return unwrap(res);
}

/** Paginate until every recipient/message row is loaded (API pages max 10k). */
export async function listAllCampaignRecipients(apiUrl, campaignId, params = {}) {
  // 5000 per page keeps typical campaigns (a few thousand numbers) to one request.
  const pageSize = Math.min(Number(params.limit) || 5000, 10000);
  let skip = 0;
  let items = [];
  let total = 0;
  for (;;) {
    const data = await listCampaignRecipients(apiUrl, campaignId, {
      ...params,
      skip,
      limit: pageSize,
    });
    const batch = data?.items ?? [];
    total = data?.total ?? batch.length;
    items = items.concat(batch);
    if (batch.length === 0 || items.length >= total) break;
    skip = items.length;
  }
  return { items, total };
}


export async function listTemplates(apiUrl) {
  const res = await axios.get(`${apiUrl}/api/templates`);
  return normalizeTemplatesList(res.data);
}

export async function getTemplatePreview(apiUrl, templateId) {
  const res = await axios.get(`${apiUrl}/api/templates/preview/${templateId}`);
  return unwrap(res);
}

/** Full previews (image, body, footer, every button) for all templates in one call. */
export async function listTemplatePreviews(apiUrl, { approvedOnly = false } = {}) {
  const res = await axios.get(`${apiUrl}/api/templates/previews`, {
    params: { approved_only: approvedOnly },
  });
  return unwrap(res) || [];
}

export async function createTemplate(apiUrl, payload) {
  const res = await axios.post(`${apiUrl}/api/templates`, payload);
  return res.data;
}

export async function uploadTemplateMedia(apiUrl, file, fileType, elementName = '') {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('file_type', fileType);
  if (elementName) formData.append('element_name', elementName);
  const res = await axios.post(`${apiUrl}/api/templates/upload-media`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return unwrap(res);
}

export function normalizeTemplatesList(raw) {
  if (Array.isArray(raw)) return raw;
  if (raw?.templates && Array.isArray(raw.templates)) return raw.templates;
  if (raw?.data?.templates && Array.isArray(raw.data.templates)) return raw.data.templates;
  if (raw?.data && Array.isArray(raw.data)) return raw.data;
  return [];
}

export function templateStatus(t) {
  return String(t?.status || t?.templateStatus || 'UNKNOWN').toUpperCase();
}

export function isTemplateApproved(t) {
  const s = templateStatus(t);
  return s === 'APPROVED' || s === 'ACTIVE';
}

export function approvedTemplates(templates) {
  return (templates || []).filter(isTemplateApproved);
}

export function templateStatusLabel(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'APPROVED' || s === 'ACTIVE') return 'Approved by Gupshup';
  if (s === 'PENDING' || s === 'SUBMITTED') return 'Pending Gupshup approval';
  if (s === 'REJECTED' || s === 'FAILED') return 'Rejected by Gupshup';
  return s || 'Unknown';
}

export function personalizePreview(text, name = 'Rahul') {
  if (!text) return '';
  return text
    .replace(/\{\{1\}\}/g, name)
    .replace(/\{\{name\}\}/gi, name);
}
